import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { useFieldControlProps } from './field';
import './styles/checkbox.css';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

export type CheckboxProps = {
  invalid?: boolean;
  /** Optional visible label when Checkbox is used outside Field. */
  label?: ReactNode;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    invalid,
    label,
    className,
    disabled,
    id,
    required,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
    ...rest
  },
  ref,
) {
  const fieldProps = useFieldControlProps({
    id,
    invalid,
    disabled,
    required,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    'aria-required': ariaRequired,
  });
  const isInvalid = fieldProps.invalid ?? invalid;
  const isDisabled = fieldProps.disabled ?? disabled;

  const input = (
    <input
      {...rest}
      ref={ref}
      type="checkbox"
      id={fieldProps.id}
      required={fieldProps.required}
      disabled={isDisabled}
      aria-describedby={fieldProps['aria-describedby']}
      aria-invalid={fieldProps['aria-invalid']}
      aria-required={fieldProps['aria-required']}
      className={cx('cev-checkbox__input', isInvalid && 'cev-checkbox__input--invalid', className)}
    />
  );

  if (label) {
    return (
      <label className="cev-checkbox" htmlFor={fieldProps.id}>
        {input}
        <span className="cev-checkbox__label">{label}</span>
      </label>
    );
  }

  return <span className="cev-checkbox cev-checkbox--bare">{input}</span>;
});
Checkbox.displayName = 'cevopCheckbox';
